import { useCallback, useEffect, useRef, useState } from "react"
import { nestClient, NestClientError } from "../../nest/client"
import { readAgentAccess, setAgentAccess, type AgentAccessResult, type AgentIdentity } from "../../wildlark/appClient"
import { AdminCapabilitiesSwitch } from "../AdminCapabilitiesSwitch"

export function AppToolsControl({ identity, compact = false, inline = false }: { identity: AgentIdentity; compact?: boolean; inline?: boolean }): JSX.Element {
    return <SessionAppToolsControl key={JSON.stringify(identity)} identity={identity} compact={compact} inline={inline} />
}

function SessionAppToolsControl({ identity, compact, inline }: { identity: AgentIdentity; compact: boolean; inline: boolean }): JSX.Element {
    const [open, setOpen] = useState(false)
    const [state, setState] = useState<AgentAccessResult | null>(null)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const readSequence = useRef(0)
    const mounted = useRef(true)
    const busy = loading || saving
    const active = compact || open

    const load = useCallback(
        async (clearError = true) => {
            const sequence = ++readSequence.current
            setLoading(true)
            if (clearError) setError(null)
            try {
                const result = await readAgentAccess(identity.sessionId)
                if (mounted.current && sequence === readSequence.current) setState(result)
            } catch (cause) {
                if (mounted.current && sequence === readSequence.current) {
                    setState(null)
                    setError(cause instanceof Error ? cause.message : String(cause))
                }
            } finally {
                if (mounted.current && sequence === readSequence.current) setLoading(false)
            }
        },
        [identity.sessionId]
    )

    useEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
            readSequence.current++
        }
    }, [])

    useEffect(() => {
        if (!active) return
        let disposed = false
        let releaseSubscription: (() => void) | undefined
        const key = `session.${identity.sessionId}`
        const unsubscribe = nestClient.on("appData.changed", (change) => {
            if (change?.namespace === "wildlark.agentAccess" && change.key === key) void load(false)
        })
        const unsubscribeStatus = nestClient.onStatusChange((status) => {
            if (status === "connected") void load(false)
        })
        void nestClient
            .retainSubscriptions([{ method: "appData.changed", appDataNamespace: "wildlark.agentAccess", appDataPrefix: key }])
            .then((release) => {
                if (disposed) release()
                else releaseSubscription = release
            })
            .catch((cause) => {
                if (!disposed) setError(`Live access updates unavailable: ${cause instanceof Error ? cause.message : String(cause)}`)
            })
        void load()
        return () => {
            disposed = true
            unsubscribe()
            unsubscribeStatus()
            releaseSubscription?.()
        }
    }, [active, identity.sessionId, load])

    const save = async (modules: string[]) => {
        if (!state || busy) return
        setSaving(true)
        setError(null)
        try {
            const result = await setAgentAccess({ ...identity, modules, role: state.access.role, expectedVersion: state.version })
            if (!mounted.current) return
            readSequence.current++
            setLoading(false)
            setState((current) => (current && current.version > result.version ? current : result))
        } catch (cause) {
            if (!mounted.current) return
            if (cause instanceof NestClientError && cause.code === "version_conflict") {
                setState(null)
                setError("App access changed in another view. Review the current settings and try again.")
                await load(false)
            } else {
                setError(cause instanceof Error ? cause.message : String(cause))
            }
        } finally {
            if (mounted.current) setSaving(false)
        }
    }

    if (compact) {
        const enabledCount = state?.access.modules.length ?? 0
        const fullyEnabled = enabledCount > 0 && state?.available.every((module) => state.access.modules.includes(module.id)) === true
        return (
            <div className="flex min-w-0 flex-wrap items-center gap-1">
                <AdminCapabilitiesSwitch
                    checked={enabledCount > 0}
                    partial={enabledCount > 0 && !fullyEnabled}
                    disabled={busy || !state || state.available.length === 0}
                    onChange={(enabled) => void save(enabled ? (state?.available.map((module) => module.id) ?? []) : [])}
                />
                {busy && (
                    <span role="status" className="sr-only">
                        Updating admin capabilities…
                    </span>
                )}
                {error && (
                    <span role="alert" className="max-w-60 text-xs text-error">
                        {error}
                    </span>
                )}
                {error && !state && (
                    <button type="button" className="btn text-xs text-primary" disabled={busy} onClick={() => void load()}>
                        Retry
                    </button>
                )}
            </div>
        )
    }

    const editor = (
        <div className="flex flex-col gap-2">
            <p className="text-muted">
                Give this thread access across projects on this computer. Computer access also reaches your other registered computers. Child threads receive
                their own access settings.
            </p>
            {state && (
                <>
                    <div className="flex gap-3">
                        <button
                            type="button"
                            className="btn text-primary"
                            disabled={busy}
                            onClick={() => void save(state.available.map((module) => module.id))}
                        >
                            Enable all
                        </button>
                        <button type="button" className="btn text-muted" disabled={busy} onClick={() => void save([])}>
                            Disable all
                        </button>
                    </div>
                    {state.available.map((module) => (
                        <label key={module.id} className="flex items-start gap-2">
                            <input
                                type="checkbox"
                                checked={state.access.modules.includes(module.id)}
                                disabled={busy}
                                onChange={(event) =>
                                    void save(
                                        event.target.checked ? [...state.access.modules, module.id] : state.access.modules.filter((id) => id !== module.id)
                                    )
                                }
                            />
                            <span>{module.description}</span>
                        </label>
                    ))}
                    <p className="text-muted">New tools appear next turn. Disabling access blocks subsequent calls immediately.</p>
                </>
            )}
            {busy && <span role="status">Updating app access…</span>}
            {error && (
                <span role="alert" className="text-error">
                    {error}
                </span>
            )}
        </div>
    )

    if (inline) {
        return (
            <>
                <button
                    type="button"
                    className="btn h-10 shrink-0 border-l border-border px-2.5 text-[11px] text-muted hover:bg-base-200 hover:text-base-content max-[900px]:h-11"
                    aria-expanded={open}
                    onClick={() => setOpen(!open)}
                >
                    App control
                </button>
                {open && <div className="basis-full border-t border-border px-3 py-2 text-xs">{editor}</div>}
            </>
        )
    }

    return (
        <div className="border-t border-border px-3 py-2 text-xs">
            <button type="button" className="btn text-muted hover:text-base-content" aria-expanded={open} onClick={() => setOpen(!open)}>
                App control
            </button>
            {open && <div className="mt-2">{editor}</div>}
        </div>
    )
}
